import { useCallback, useState } from "react";
import { servicesApi } from "@/api/client";
import { useServices } from "@/hooks/useApi";
import { LlmFields, type LlmProvider } from "./LlmFields";
import { defaultModelChoice } from "./llmDefaultChoice";
import { useAnthropicFields } from "./useAnthropicFields";
import { useBedrockFields } from "./useBedrockFields";
import { useOpenAiFields } from "./useOpenAiFields";

// The "LLM" provider category: one AI provider picked inside the card, each
// keeping its own field state so switching back and forth loses nothing.
export function useLlmFields() {
  const [aiProvider, setAiProvider] = useState<LlmProvider>("anthropic");
  const [setAsDefault, setSetAsDefault] = useState(false);

  const hooks = {
    anthropic: useAnthropicFields(),
    bedrock: useBedrockFields(),
    openai: useOpenAiFields(),
  };
  const active = hooks[aiProvider];

  // What the default-model box does depends on the LLMs that already exist
  // (see defaultModelChoice); `data` stays undefined if the list fails to
  // load, which leaves the choice to the user.
  const { data: llmServices, isLoading } = useServices({ kind: "llm" });
  const choice = defaultModelChoice(
    llmServices?.length,
    isLoading,
    setAsDefault,
  );

  // Activating also writes the choice down, so the first service shows as
  // Active instead of relying on the engine's implicit fallback.
  const afterCreate = useCallback(
    async (name: string) => {
      if (choice.checked) await servicesApi.activate(name);
    },
    [choice.checked],
  );

  const fields = (
    <LlmFields
      aiProvider={aiProvider}
      setAiProvider={setAiProvider}
      checked={choice.checked}
      disabled={choice.locked}
      onCheckedChange={setSetAsDefault}
      showLockedHint={choice.firstLlm}
      providerFields={active.fields}
    />
  );

  return {
    provider: aiProvider,
    fields,
    toPayload: active.toPayload,
    afterCreate,
  };
}
